// Hooks do CONSUMO MEDIDO: custo operacional por dia, assento por usuário e a
// conta do mês. Mesmo padrão de use-dispositions.ts:
//   - `src/api/costs.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// As chaves abaixo são as que já estavam nas três telas de Financeiro (Custos,
// Custo por usuário e Consumo/Pagamentos), string por string — centralizar não é
// renomear. O React Query invalida por PREFIXO, e uma chave que divergisse uma
// letra viraria dado velho na tela, sem erro nenhum.
import { useQuery } from "@tanstack/react-query";
import {
  getBilling,
  getCostRates,
  getCostSummary,
  getCostSummaryByUser,
  type CostSummaryQuery,
} from "@/api/costs";

// As chaves DESTE domínio. As divergências herdadas que valem leitura:
//
//  - as quatro são strings SOLTAS, sem um prefixo ["costs", …] em comum:
//    ["cost-rates"], ["cost-summary", …], ["cost-summary-by-user", …] e
//    ["billing"]. Não há um invalidador que derrube o domínio inteiro, e é bom
//    que não haja: nenhuma delas tem mutação: são quatro leituras de medição, e
//    quem as derruba é outra coisa (ver `billing` abaixo). Agrupá-las sob um
//    prefixo comum criaria um poder de invalidação que ninguém pediu.
//  - `summary` e `summaryByUser` são chaveadas por DUAS partes soltas (from e
//    to), não por um objeto — é o que as telas faziam, e é o que mantém a
//    entrada de cache estável quando o objeto de período é remontado a cada
//    render. Sem período (a tela ainda não escolheu um), as partes ficam
//    `undefined` e a query não roda: também é o que já acontecia.
//  - `billing` é ["billing"] e NÃO carrega o número de meses, embora
//    `getBilling` receba um. As duas telas que a leem pedem 12, então a entrada
//    é uma só; pedir 6 devolveria o cache de 12. Divergência pré-existente,
//    preservada e não "consertada" para ["billing", months] — isso partiria a
//    entrada em duas e mudaria quem `useDeleteCallRecording` derruba.
//  - `billing` é justamente a chave que `src/hooks/use-calls.ts` invalida ao
//    apagar uma gravação (armazenamento entra na conta do mês). Enquanto este
//    domínio não existia, ela ficava literal lá; agora aponta para cá.
export const costKeys = {
  rates: ["cost-rates"] as const,
  summary: (from?: string, to?: string) => ["cost-summary", from, to] as const,
  summaryByUser: (from?: string, to?: string) => ["cost-summary-by-user", from, to] as const,
  billing: ["billing"] as const,
};

// Tarifas (só leitura — o preço é nosso e vem do env do back). Sem `enabled`: o
// diálogo de tarifas só monta quando abre, e é ele quem controla o momento.
export function useCostRates() {
  return useQuery({
    queryKey: costKeys.rates,
    queryFn: getCostRates,
  });
}

// Custo operacional do período. `null` = a tela ainda não tem período escolhido:
// a query não roda e a chave fica com as duas partes `undefined`, exatamente
// como o `enabled: hasRange` da tela fazia.
export function useCostSummary(range: CostSummaryQuery | null) {
  return useQuery({
    queryKey: costKeys.summary(range?.from, range?.to),
    queryFn: () => getCostSummary(range ?? {}),
    enabled: range !== null,
  });
}

// Assento por usuário no mesmo período. Mesma regra de `null` do hook acima.
export function useCostSummaryByUser(range: CostSummaryQuery | null) {
  return useQuery({
    queryKey: costKeys.summaryByUser(range?.from, range?.to),
    queryFn: () => getCostSummaryByUser(range ?? {}),
    enabled: range !== null,
  });
}

// A conta do mês por competência. `months` NÃO entra na chave (ver `costKeys`):
// as duas telas pedem 12, e mudar isso partiria a entrada de cache que o
// apagar-gravação derruba.
export function useBilling(months = 12) {
  return useQuery({
    queryKey: costKeys.billing,
    queryFn: () => getBilling(months),
  });
}
